import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Loader from "../components/UI/Loader";
import PossibleDuplicateClient from "../models/serviceModels/PossibleDuplicateClient";
import { dismissDuplicate, getAllPossibleDuplicateClients, getDismissedDuplicateClients, revertDismissedDuplicate } from "../services/clientsService";
import { formatJSONDateWithoutTime } from "../utils/dateHelpers";
import './PossibleDuplicateParticipants.css';
import { format } from "date-fns";
import Checkbox from "../components/UI/Checkbox";
import Button from "../components/UI/Button";
import { v4 as uuidv4 } from 'uuid';
import DismissedClientDuplicate from "../models/serviceModels/DismissedClientDuplicate";
import toast from "react-hot-toast";
import { OrganizationContext } from '../App';
import { isDemographicVisible } from '../utils/demographicsSchema';

const ProcessDuplicateParticipants = () => {
    const organization = useContext(OrganizationContext);
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const showEmail = isDemographicVisible(organization.value, 'email');
    const navigate = useNavigate();
    const [apiError, setApiError] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [reverting, setReverting] = useState<string[]>([]);
    const [dismissing, setDismissing] = useState<string[]>([]);
    const [showDismissedDuplicates, setShowDismissedDuplicates] = useState(false);
    const [selectedClients, setSelectedClients] = useState<{ sourceClient: PossibleDuplicateClient, possibleDuplicates: PossibleDuplicateClient[] }>();
    const [duplicateGroups, setDuplicateGroups] = useState<{ sourceClient: PossibleDuplicateClient, possibleDuplicates: PossibleDuplicateClient[] }[]>();
    const [dismissedDuplicates, setDismissedDuplicates] = useState<DismissedClientDuplicate[]>();

    useEffect(() => {
        document.title = "Duplicate Participants"
    }, []);

    useEffect(() => {
        setLoading(true);

        Promise.all([getAllPossibleDuplicateClients(), getDismissedDuplicateClients()]).then(([pdc, ddc]) => {
            setDuplicateGroups(pdc);
            setDismissedDuplicates(ddc);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoading(false);
        })
    }, []);

    const handleOnClientSelected = (checked: boolean, selectedDuplicate: { source: PossibleDuplicateClient, duplicate: PossibleDuplicateClient }) => {
        if (checked) {
            if (!selectedClients || selectedDuplicate.source.id !== selectedClients.sourceClient.id) {
                setSelectedClients({
                    sourceClient: selectedDuplicate.source,
                    possibleDuplicates: [selectedDuplicate.duplicate]
                });
            } else {
                setSelectedClients({
                    sourceClient: selectedClients.sourceClient,
                    possibleDuplicates: [
                        ...selectedClients.possibleDuplicates,
                        selectedDuplicate.duplicate
                    ]
                });
            }
        } else if (selectedClients && selectedDuplicate.source.id === selectedClients.sourceClient.id) {
            setSelectedClients({
                sourceClient: selectedClients.sourceClient,
                possibleDuplicates: [
                    ...selectedClients.possibleDuplicates.filter(d => d.id !== selectedDuplicate.duplicate.id)
                ]
            });
        }
    };

    const handleOnDismissDuplicate = (duplicate: { source: PossibleDuplicateClient, target: PossibleDuplicateClient }) => {
        const dismissingKey = `${duplicate.source.id}_${duplicate.target.id}`;
        setDismissing([
            ...dismissing,
            dismissingKey
        ]);
        setApiError(undefined);

        dismissDuplicate({
            clientId1: duplicate.source.id,
            clientId2: duplicate.target.id
        }).then(r => {
            setDismissedDuplicates(r);
            getAllPossibleDuplicateClients().then(dc => {
                setDuplicateGroups(dc);
                toast.success('Successfully dismissed')
            }).catch(e => {
                console.error(e);
                setApiError(e);
                toast.error('Failed to referesh duplicates');
            }).finally(() => {
                setDismissing([
                    ...dismissing.filter(d => d !== dismissingKey)
                ]);
            });
        }).catch(e => {
            console.error(e);
            setApiError(e);
            toast.error('Failed to dismiss duplicate');
            setDismissing([
                ...dismissing.filter(d => d !== dismissingKey)
            ]);
        })
    };

    const handleOnMerge = () => {
        if (!selectedClients || selectedClients.possibleDuplicates.length === 0) {
            console.warn('No clients selected for merge');
            return;
        }

        const mergeId = uuidv4()
        localStorage.setItem(`mergeParticipants_${mergeId}`, JSON.stringify({
            selectedClientId: selectedClients.sourceClient.id,
            additionalClientIds: selectedClients.possibleDuplicates.map(d => d.id),
            expiresOn: new Date().getTime() + (1000 * 60 * 60 * 24 * 5)
        }));
        navigate(`/participants/merge/${mergeId}/compare`);
    };

    const handleOnReverting = (dismissedDuplicate: DismissedClientDuplicate) => {
        if (reverting.includes(dismissedDuplicate.id)) {
            return;
        }

        setReverting([
            ...reverting,
            dismissedDuplicate.id
        ]);

        setApiError(undefined);

        revertDismissedDuplicate(dismissedDuplicate.id).then(r => {
            toast.success('Successfully reverted');
            setDismissedDuplicates(r);
            getAllPossibleDuplicateClients().then(r => {
                setDuplicateGroups(r);
            }).catch(e => {
                console.error(e);
                setApiError(e);
                toast.error('Failed to refresh duplicates');
            }).finally(() => {
                setReverting([
                    ...reverting.filter(r => r !== dismissedDuplicate.id)
                ]);
            })
        }).catch(e => {
            console.error(e);
            setApiError(e);
            toast.error('Failed to reverse');
            setReverting([
                ...reverting.filter(r => r !== dismissedDuplicate.id)
            ]);
        });
    }

    return (
        <div className="section">
            <section id="main-content" tabIndex={-1}>
                <div className="row justify-content-center">
                    <div className="column-medium-10">
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <h1>Possible Duplicates Report</h1>
                            </div>
                        </div>
                        <ApiErrorMessages error={apiError} />
                        {dismissedDuplicates && <Button id="toggle-dismissed-duplicates"
                            onClick={() => setShowDismissedDuplicates(!showDismissedDuplicates)}
                            className="secondary"
                            text={showDismissedDuplicates ? `Hide Dismissed Duplicates (${dismissedDuplicates.length})` : `Show Dismissed Duplicates (${dismissedDuplicates.length})`}
                            />}
                        <div className="row margin-top-5">
                            {showDismissedDuplicates && <div className="column-large-3 border padding-3">
                                <h2>Dismissed Duplicates</h2>
                                {loading && <Loader />}
                                {dismissedDuplicates && <>
                                    {dismissedDuplicates.length === 0 && <div>
                                        <div className="row justify-content-center">
                                            <div className="column-auto">
                                                No dismissed duplicates found
                                            </div>
                                        </div>
                                    </div>}
                                    {dismissedDuplicates.length !== 0 && <div className="list-item-container">
                                        {dismissedDuplicates.map(d => {
                                            return <div key={d.id} className="list-item">
                                                <div className="text-bold margin-bottom-3">Dismissed - {format(new Date(d.created), 'MM/dd/yyyy')}</div>
                                                <div className="row">
                                                    <div className="column-6"><Link to={`/participants/${d.clientId1}/profile-demographics`} target="_blank">{d.clientLastName1}, {d.clientFirstName1}</Link></div>
                                                    <div className="column-6">DOB: {d.clientDateOfBirth1 && formatJSONDateWithoutTime(d.clientDateOfBirth1, 'MM/dd/yyyy')}</div>
                                                </div>
                                                <div className="row">
                                                    <div className="column-6"><Link to={`/participants/${d.clientId2}/profile-demographics`} target="_blank">{d.clientLastName2}, {d.clientFirstName2}</Link></div>
                                                    <div className="column-6">DOB: {d.clientDateOfBirth2 && formatJSONDateWithoutTime(d.clientDateOfBirth2, 'MM/dd/yyyy')}</div>
                                                </div>
                                                <div className="row justify-content-end margin-top-3">
                                                    <div className="column-auto">
                                                        <Button id={`revert-dismiss-${d.id}`}
                                                            text="Revert"
                                                            className="action small"
                                                            onClick={() => handleOnReverting(d)}
                                                            disabled={reverting.includes(d.id)}
                                                            loading={reverting.includes(d.id)} />
                                                    </div>
                                                </div>
                                            </div>
                                        })}
                                    </div>}
                                </>}
                            </div>}
                            <div className="column padding-3">
                                <h2>Possible Duplicate Participants</h2>
                                {loading && <Loader />}
                                {duplicateGroups && <>
                                    {duplicateGroups.length === 0 &&
                                        <div className="row justify-content-center">
                                            <div className="column-auto">
                                                No possible duplicates found
                                            </div>
                                        </div>}
                                        
                                    {duplicateGroups.length !== 0 && <div className="row justify-content-end margin-vertical-3 floating-section">
                                        <div className="column-auto">
                                            <Button id="start-merge"
                                                onClick={handleOnMerge}
                                                text="Begin Merge"
                                                disabled={!selectedClients || selectedClients.possibleDuplicates.length === 0 || reverting.length !== 0} />
                                        </div>
                                    </div>}
                                    {duplicateGroups.length !== 0 && <div className="duplicates-container">
                                        <div className="list-item-container">
                                            {duplicateGroups.map(d => {
                                                return <>
                                                    <div className="list-item margin-top-5">
                                                        <div className="row align-items-center">
                                                            <div className="column">
                                                                <div className="row margin-vertical-3 align-items-center justify-content-between">
                                                                    <div className="column-4">
                                                                        <div className="row align-items-center">
                                                                            <div className="column-6 text-larger-x1-2">
                                                                                <Link to={`/participants/${d.sourceClient.id}/profile-demographics`} target="_blank">{d.sourceClient.lastName}, {d.sourceClient.firstName}</Link>
                                                                            </div>
                                                                            <div className="column-6 text-sub ">
                                                                                <div className="row justify-content-between">
                                                                                    <div className="column-4"><label>Created</label></div>
                                                                                    <div className="column-auto">{format(new Date(d.sourceClient.created), 'MM/dd/yyyy')}</div>
                                                                                </div>
                                                                                <div className="row justify-content-between">
                                                                                    <div className="column-4"><label>Updated</label></div>
                                                                                    <div className="column-auto">{d.sourceClient.updated && format(new Date(d.sourceClient.updated), 'MM/dd/yyyy')}</div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="row margin-vertical-3">
                                                                    {showPhone && <div className="column-medium-4">
                                                                        <label>Birth Date</label>
                                                                        <div>{d.sourceClient.birthDate && formatJSONDateWithoutTime(d.sourceClient.birthDate, 'MM/dd/yyyy')}</div>
                                                                    </div>}
                                                                    {showEmail && <div className="column-medium-4">
                                                                        <label>Phone Number</label>
                                                                        <div>{d.sourceClient.phoneNumber}</div>
                                                                    </div>}
                                                                    <div className="column-medium-4">
                                                                        <label>Email</label>
                                                                        <div>{d.sourceClient.email}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {d.possibleDuplicates.map(pd => {
                                                        return <div className="list-item sub-list-item">
                                                            <div className="row align-items-center">
                                                                <div className="column-auto">
                                                                    <Checkbox id={`select-duplicate-${pd.id}`}
                                                                        checked={selectedClients && selectedClients.possibleDuplicates.includes(pd)}
                                                                        onChange={(checked) => handleOnClientSelected(checked, { source: d.sourceClient, duplicate: pd })}
                                                                        containerClassName="margin-top-0 unboxed" />
                                                                </div>
                                                                <div className="column">
                                                                    <div className="row margin-vertical-3 align-items-center">
                                                                        <div className="column-6">
                                                                            <div className="row align-items-center">
                                                                                <div className="column-6 text-larger-x1-2">
                                                                                    <Link to={`/participants/${pd.id}/profile-demographics`} target="_blank">{pd.lastName}, {pd.firstName}</Link>
                                                                                </div>
                                                                                <div className="column-6 text-sub ">
                                                                                    <div className="row justify-content-between">
                                                                                        <div className="column-4"><label>Created</label></div>
                                                                                        <div className="column-auto">{format(new Date(pd.created), 'MM/dd/yyyy')}</div>
                                                                                    </div>
                                                                                    <div className="row justify-content-between">
                                                                                        <div className="column-4"><label>Updated</label></div>
                                                                                        <div className="column-auto">{pd.updated && format(new Date(pd.updated), 'MM/dd/yyyy')}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="row margin-vertical-3">
                                                                        {showPhone && <div className="column-medium-4">
                                                                            <label>Birth Date</label>
                                                                            <div>{pd.birthDate && formatJSONDateWithoutTime(pd.birthDate, 'MM/dd/yyyy')}</div>
                                                                        </div>}
                                                                        {showEmail && <div className="column-medium-4">
                                                                            <label>Phone Number</label>
                                                                            <div>{pd.phoneNumber}</div>
                                                                        </div>}
                                                                        <div className="column-medium-4">
                                                                            <label>Email</label>
                                                                            <div>{pd.email}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="column-auto">
                                                                    <Button id={`dismiss-duplicate-${pd.id}`}
                                                                        className="action small"
                                                                        text="Dismiss Duplicate"
                                                                        disabled={dismissing.includes(`${d.sourceClient.id}_${pd.id}`) || (selectedClients && selectedClients.possibleDuplicates.length !== 0)}
                                                                        loading={dismissing.includes(`${d.sourceClient.id}_${pd.id}`)}
                                                                        onClick={() => handleOnDismissDuplicate({
                                                                            source: d.sourceClient,
                                                                            target: pd
                                                                        })} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    })}
                                                </>
                                            })}
                                        </div >
                                    </div>}
                                </>}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProcessDuplicateParticipants;
